import { useState, useCallback, useRef, useEffect, useLayoutEffect } from "react";
import {
  loadReviewResults,
  loadReviewResultsFromBackend,
  saveReviewResults,
} from "../services/storageService";
import { exportSlideAsImage, goToSlide, listSlides } from "../services/slideService";
import { reviewDeckForQA, SlideQAResult } from "../services/ai";
import {
  createPptxLangfuseRunContext,
  postPptxLangfuseOutcome,
  type PptxLangfuseOutcome,
} from "../langfuseTrace";
import {
  trackDeckReviewCompleted,
  type TrackDeckReviewCompletedParams,
} from "../services/analytics/product";

export interface ReviewProgress {
  current: number;
  total: number;
  phase: "export" | "review";
}

// Langfuse and the analytics catalog spell these three outcomes differently.
// The analytics endpoint rejects an unknown `status` with a 400 that only
// console-warns, so drift here would silently stop the event reaching Amplitude.
const DECK_REVIEW_ANALYTICS_STATUS: Record<
  PptxLangfuseOutcome["status"],
  TrackDeckReviewCompletedParams["status"]
> = {
  completed: "complete",
  failed: "error",
  cancelled: "cancelled",
};

interface UseDeckReviewProps {
  log: (message: string) => void;
  isAuthenticated: boolean;
  accountScopeKey?: string | null;
  /** @deprecated App should pass accountScopeKey directly. */
  storageUserId?: string | null;
}

interface ReviewScopeOwnership {
  accountScopeKey: string | null;
  generation: number;
  isAuthenticated: boolean;
}

function isSameReviewScope(
  left: ReviewScopeOwnership,
  right: ReviewScopeOwnership
): boolean {
  return (
    left.accountScopeKey === right.accountScopeKey &&
    left.generation === right.generation &&
    left.isAuthenticated === right.isAuthenticated
  );
}

export const useDeckReview = ({
  log,
  isAuthenticated,
  accountScopeKey: explicitAccountScopeKey,
  storageUserId,
}: UseDeckReviewProps) => {
  const accountScopeKey =
    explicitAccountScopeKey !== undefined
      ? explicitAccountScopeKey
      : storageUserId ?? null;
  const logRef = useRef(log);
  useEffect(() => {
    logRef.current = log;
  });

  const [renderedScope, setRenderedScope] = useState<ReviewScopeOwnership>({
    accountScopeKey,
    generation: 0,
    isAuthenticated,
  });
  const currentScopeRef = useRef<ReviewScopeOwnership>({
    accountScopeKey,
    generation: 0,
    isAuthenticated,
  });
  if (
    renderedScope.accountScopeKey !== accountScopeKey ||
    renderedScope.isAuthenticated !== isAuthenticated
  ) {
    setRenderedScope({
      accountScopeKey,
      isAuthenticated,
      generation: renderedScope.generation + 1,
    });
  }
  useLayoutEffect(() => {
    currentScopeRef.current = renderedScope;
  }, [renderedScope]);

  const [reviewResultsState, setReviewResultsState] = useState<SlideQAResult[]>([]);
  const [isReviewingState, setIsReviewingState] = useState(false);
  const [reviewProgressState, setReviewProgressState] = useState<ReviewProgress | null>(null);
  const [slideCountState, setSlideCountState] = useState<number>(0);
  const [selectedSlidesState, setSelectedSlidesState] = useState<number[]>([]);
  const [showSlideSelectorState, setShowSlideSelectorState] = useState(false);

  const mountedRef = useRef(true);
  const stateOwnerRef = useRef(renderedScope);
  const [stateOwner, setStateOwner] = useState(renderedScope);
  const reviewResultsRef = useRef<SlideQAResult[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  if (!isSameReviewScope(stateOwner, renderedScope)) {
    setStateOwner(renderedScope);
    setReviewResultsState([]);
    setIsReviewingState(false);
    setReviewProgressState(null);
    setSlideCountState(0);
    setSelectedSlidesState([]);
    setShowSlideSelectorState(false);
  }

  const scopeIsCurrent = useCallback((scope: ReviewScopeOwnership) => {
    return mountedRef.current && isSameReviewScope(currentScopeRef.current, scope);
  }, []);

  const updateReviewResults = useCallback(
    (
      scope: ReviewScopeOwnership,
      resultsOrUpdater: SlideQAResult[] | ((prev: SlideQAResult[]) => SlideQAResult[]),
      persist: boolean
    ): boolean => {
      if (!scopeIsCurrent(scope)) return false;
      const previousResults = isSameReviewScope(stateOwnerRef.current, scope)
        ? reviewResultsRef.current
        : [];
      const nextResults =
        typeof resultsOrUpdater === "function"
          ? resultsOrUpdater(previousResults)
          : resultsOrUpdater;
      if (!scopeIsCurrent(scope)) return false;

      stateOwnerRef.current = scope;
      setStateOwner(scope);
      reviewResultsRef.current = nextResults;
      setReviewResultsState(nextResults);
      if (
        persist &&
        scope.accountScopeKey !== null &&
        scopeIsCurrent(scope)
      ) {
        saveReviewResults(nextResults, scope.accountScopeKey);
      }
      return true;
    },
    [scopeIsCurrent]
  );

  useLayoutEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
    };
  }, []);

  useLayoutEffect(() => {
    let cancelled = false;
    const scope = renderedScope;

    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    if (!scopeIsCurrent(scope)) {
      return () => {
        cancelled = true;
      };
    }

    stateOwnerRef.current = scope;
    reviewResultsRef.current = [];

    if (!scope.isAuthenticated || scope.accountScopeKey === null) {
      return () => {
        cancelled = true;
      };
    }

    const localResults = loadReviewResults(scope.accountScopeKey) as SlideQAResult[];
    if (!cancelled && scopeIsCurrent(scope)) {
      // Hydrate persisted external state after committing the new ownership and aborting the old request.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      updateReviewResults(scope, localResults, false);
    }

    void loadReviewResultsFromBackend(scope.accountScopeKey).then((remote) => {
      if (cancelled || !scopeIsCurrent(scope) || !Array.isArray(remote)) return;
      updateReviewResults(scope, remote as SlideQAResult[], false);
    });

    return () => {
      cancelled = true;
      abortControllerRef.current?.abort();
    };
  }, [renderedScope, scopeIsCurrent, updateReviewResults]);

  const openSlideSelector = useCallback(async () => {
    const scope = currentScopeRef.current;
    if (!scopeIsCurrent(scope)) return;
    // Slide-count + all-selected default only need a slide-id list; skip the
    // full RunOfficeExtractor trip and use the cached slide-list fast path.
    const slides = await listSlides();
    if (!scopeIsCurrent(scope)) return;
    stateOwnerRef.current = scope;
    setStateOwner(scope);
    setSlideCountState(slides.length);
    setSelectedSlidesState(slides.map((_, i) => i));
    setShowSlideSelectorState(true);
  }, [scopeIsCurrent]);

  const cancelSlideSelector = useCallback(() => {
    const scope = currentScopeRef.current;
    if (!scopeIsCurrent(scope)) return;
    stateOwnerRef.current = scope;
    setStateOwner(scope);
    setShowSlideSelectorState(false);
  }, [scopeIsCurrent]);

  const setSelectedSlides = useCallback(
    (value: number[] | ((previous: number[]) => number[])) => {
      const scope = currentScopeRef.current;
      if (!scopeIsCurrent(scope)) return;
      setStateOwner(scope);
      setSelectedSlidesState((previous) => {
        if (!scopeIsCurrent(scope)) return previous;
        const ownedPrevious = isSameReviewScope(stateOwnerRef.current, scope)
          ? previous
          : [];
        stateOwnerRef.current = scope;
        return typeof value === "function" ? value(ownedPrevious) : value;
      });
    },
    [scopeIsCurrent]
  );

  const startReview = useCallback(
    async (slidesToReview: number[]) => {
      const reviewScope = currentScopeRef.current;
      if (
        !scopeIsCurrent(reviewScope) ||
        !reviewScope.isAuthenticated ||
        reviewScope.accountScopeKey === null
      ) {
        return;
      }

      const log = logRef.current;
      const controller = new AbortController();
      const previousController = abortControllerRef.current;
      abortControllerRef.current = controller;
      previousController?.abort();

      const runIsOwned = () =>
        scopeIsCurrent(reviewScope) &&
        abortControllerRef.current === controller;
      const runCanWrite = () => runIsOwned() && !controller.signal.aborted;

      const langfuseStartedAt = Date.now();
      const langfuseRunContext = createPptxLangfuseRunContext({
        mode: "review",
        hasContext: false,
      });
      langfuseRunContext.deck = { slideCount: slidesToReview.length };
      let outcomeReported = false;
      let settled = false;
      /**
       * Reports one terminal outcome, at most once per review.
       *
       * `analyticsErrorType` is a controlled slug rather than `outcome.errorType`,
       * because raw review errors can embed slide content that must not reach
       * Amplitude. `langfuse: false` covers an outcome Langfuse did not record
       * before this change; Langfuse is the source of truth for run outcomes, so
       * its numbers must not move here even where analytics needs the event.
       */
      const postOutcomeOnce = (
        outcome: PptxLangfuseOutcome,
        options: { analyticsErrorType?: string; langfuse?: boolean } = {}
      ) => {
        const { analyticsErrorType, langfuse = true } = options;
        if (outcomeReported || !runIsOwned()) return;
        outcomeReported = true;
        if (langfuse) postPptxLangfuseOutcome(langfuseRunContext, outcome);
        trackDeckReviewCompleted({
          requestedCount: slidesToReview.length,
          durationMs: outcome.durationMs,
          status: DECK_REVIEW_ANALYTICS_STATUS[outcome.status],
          ...(outcome.slidesReviewed !== undefined
            ? { slidesReviewed: outcome.slidesReviewed }
            : {}),
          ...(outcome.issuesFound !== undefined ? { issuesFound: outcome.issuesFound } : {}),
          ...(analyticsErrorType ? { errorType: analyticsErrorType } : {}),
        });
      };

      const reportCancellation = (langfuse: boolean) => {
        if (!controller.signal.aborted || !runIsOwned()) return;
        postOutcomeOnce(
          {
            status: "cancelled",
            durationMs: Date.now() - langfuseStartedAt,
          },
          { langfuse }
        );
      };

      stateOwnerRef.current = reviewScope;
      setShowSlideSelectorState(false);
      setIsReviewingState(true);
      setReviewProgressState(null);
      updateReviewResults(reviewScope, [], true);
      log(`🔍 Starting review of ${slidesToReview.length} slides...`);

      try {
        const slideImages: { index: number; base64: string }[] = [];

        for (let i = 0; i < slidesToReview.length; i++) {
          if (!runCanWrite()) {
            reportCancellation(true);
            return;
          }

          const slideIndex = slidesToReview[i];
          log(`📸 Exporting slide ${slideIndex + 1} (${i + 1}/${slidesToReview.length})...`);
          setReviewProgressState({
            current: i + 1,
            total: slidesToReview.length,
            phase: "export",
          });
          const base64 = await exportSlideAsImage(slideIndex);
          if (!runCanWrite()) {
            reportCancellation(true);
            return;
          }
          slideImages.push({ index: slideIndex, base64 });
        }

        if (!runCanWrite()) {
          reportCancellation(true);
          return;
        }

        log(
          `✓ Exported ${slideImages.length} slides, starting AI review with cross-slide consistency checking...`
        );

        await reviewDeckForQA(
          slideImages,
          {
            onProgress: (current, total) => {
              if (!runCanWrite() || settled) return;
              setReviewProgressState({ current, total, phase: "review" });
            },
            onSlideResult: (result) => {
              if (!runCanWrite() || settled) return;
              console.log(
                "[REVIEW] Got slide result:",
                result.slideIndex,
                result.issues?.length || 0,
                "issues",
                result.keyFacts?.length || 0,
                "key facts"
              );
              const didUpdate = updateReviewResults(reviewScope, (prev) => {
                const newResults = [...prev, result];
                console.log("[REVIEW] Results state now:", newResults.length, "slides");
                return newResults;
              }, true);
              if (!didUpdate || !runCanWrite() || settled) return;
              const issueCount = result.issues?.length || 0;
              const consistencyIssues =
                result.issues?.filter((i) => i.type === "consistency").length || 0;
              logRef.current(
                `  Slide ${result.slideIndex + 1}: ${issueCount} issue${issueCount !== 1 ? "s" : ""} (${consistencyIssues} consistency) - score: ${result.overallScore}/10`
              );
            },
            onComplete: (results) => {
              if (!runCanWrite() || settled) return;
              settled = true;
              console.log("[REVIEW] onComplete called with", results.length, "results");
              if (!updateReviewResults(reviewScope, results, true)) return;
              const totalIssues = results.reduce((sum, r) => sum + (r.issues?.length || 0), 0);
              const consistencyIssues = results.reduce(
                (sum, r) => sum + (r.issues?.filter((i) => i.type === "consistency").length || 0),
                0
              );
              const avgScore =
                results.length > 0
                  ? (
                      results.reduce((sum, r) => sum + (r.overallScore || 0), 0) / results.length
                    ).toFixed(1)
                  : 0;
              logRef.current(
                `✓ Review complete! ${totalIssues} issues (${consistencyIssues} cross-slide consistency) across ${results.length} slides (avg score: ${avgScore}/10)`
              );
              setIsReviewingState(false);
              setReviewProgressState(null);
              postOutcomeOnce({
                status: "completed",
                durationMs: Date.now() - langfuseStartedAt,
                slidesReviewed: results.length,
                issuesFound: totalIssues,
              });
            },
            onError: (error) => {
              if (!runCanWrite() || settled) return;
              settled = true;
              logRef.current(`⚠️ Review error: ${error}`);
              setIsReviewingState(false);
              setReviewProgressState(null);
              postOutcomeOnce(
                {
                  status: "failed",
                  durationMs: Date.now() - langfuseStartedAt,
                  errorType: error,
                },
                { analyticsErrorType: "review_error" }
              );
            },
            onThinking: (slideIdx, text) => {
              if (!runCanWrite() || settled) return;
              logRef.current(`💭 Slide ${slideIdx + 1} reasoning: ${text}`);
            },
          },
          controller.signal,
          langfuseRunContext
        );

        // `reviewDeckForQA` returns without calling `onComplete` or `onError`
        // when the signal trips mid-loop, so cancelling during the AI phase
        // reports no outcome at all unless it is caught here.
        if (controller.signal.aborted) {
          reportCancellation(false);
        }
      } catch (err) {
        if (controller.signal.aborted) {
          reportCancellation(true);
          return;
        }
        if (!runCanWrite() || settled) return;
        settled = true;
        logRef.current(
          `⚠️ Error during review: ${err instanceof Error ? err.message : String(err)}`
        );
        setIsReviewingState(false);
        setReviewProgressState(null);
        postOutcomeOnce(
          {
            status: "failed",
            durationMs: Date.now() - langfuseStartedAt,
            errorType: err instanceof Error ? err.message : String(err),
          },
          { analyticsErrorType: "review_exception" }
        );
      } finally {
        if (!runCanWrite()) {
          return;
        }
        if (!settled) {
          setIsReviewingState(false);
          setReviewProgressState(null);
        }
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
      }
    },
    [scopeIsCurrent, updateReviewResults]
  );

  const clearReviewAndGoBack = useCallback(() => {
    const scope = currentScopeRef.current;
    if (!scopeIsCurrent(scope)) return;
    abortControllerRef.current?.abort();
    stateOwnerRef.current = scope;
    setStateOwner(scope);
    updateReviewResults(scope, [], true);
    setIsReviewingState(false);
    setReviewProgressState(null);
    setShowSlideSelectorState(false);
    setSelectedSlides([]);
  }, [scopeIsCurrent, setSelectedSlides, updateReviewResults]);

  const clearResults = useCallback(() => {
    const scope = currentScopeRef.current;
    if (!scopeIsCurrent(scope)) return;
    updateReviewResults(scope, [], true);
  }, [scopeIsCurrent, updateReviewResults]);

  const stateIsCurrent = isSameReviewScope(stateOwner, renderedScope);

  return {
    reviewResults: stateIsCurrent ? reviewResultsState : [],
    isReviewing: stateIsCurrent ? isReviewingState : false,
    reviewProgress: stateIsCurrent ? reviewProgressState : null,
    slideCount: stateIsCurrent ? slideCountState : 0,
    selectedSlides: stateIsCurrent ? selectedSlidesState : [],
    setSelectedSlides,
    showSlideSelector: stateIsCurrent ? showSlideSelectorState : false,
    openSlideSelector,
    cancelSlideSelector,
    startReview,
    clearReviewAndGoBack,
    clearResults,
    goToSlide,
  };
};
